import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import PageHeader1 from "../../components/common/PageHeader1";
import {
  submitQuestionApi,
  getQuestionByIdApi,
} from "../../services/DailyCheckInQuestions";

/* =======================
   CHECK-IN KEYS & VALUES
======================= */

const CHECKIN_KEYS = [
  "Emotional Load",
  "Energy Level",
  "Body Awareness",
  "Nervous System State",
  "Connection State",
];

const CHECKIN_VALUES_MAP = {
  "Emotional Load": ["low", "high"],
  "Energy Level": ["adequate", "low"],
  "Body Awareness": ["present", "disconnected"],
  "Nervous System State": ["regulated", "dysregulated"],
  "Connection State": ["connected", "disconnected"],
};

function AddCheckinQuestionList() {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm();

  // ✅ Options state (same pattern as onboarding)
  const [options, setOptions] = useState([
    { label: "", key: "", value: "" },
  ]);

  /* =======================
     Edit Mode Load
  ======================= */
  useEffect(() => {
    if (!id) return;

    (async () => {
      const res = await getQuestionByIdApi(id);
      if (res?.data) {
        const r = res.data;
        setValue("question", r.questionText);

        const formattedOptions = r.options.map((opt) =>
          typeof opt === "string"
            ? { label: opt, key: "", value: "" }
            : {
                label: opt.label || "",
                key: opt.key || "",
                value: opt.value || "",
              }
        );

        setOptions(
          formattedOptions.length
            ? formattedOptions
            : [{ label: "", key: "", value: "" }]
        );
      }
    })();
  }, [id, setValue]);

  /* =======================
     Handlers
  ======================= */
  const handleOptionChange = (index, field, value) => {
    const updated = [...options];
    updated[index][field] = value;

    // key change → reset value
    if (field === "key") {
      updated[index].value = "";
    }

    setOptions(updated);
  };

  const addOption = () =>
    setOptions([...options, { label: "", key: "", value: "" }]);

  const removeOption = (index) => {
    const updated = [...options];
    updated.splice(index, 1);
    setOptions(updated);
  };

  /* =======================
     Submit
  ======================= */
  const onSubmit = async (data) => {
    const payload = {
      ...data,
      options,
    };

    if (id) payload._id = id;

    const res = await submitQuestionApi(payload);
    if (res) {
      if (!id) {
        reset();
        setOptions([{ label: "", key: "", value: "" }]);
      } else {
        navigate("/daily-checkin-questions");
      }
    }
  };

  return (
    <div className="container">
      <PageHeader1
        pagetitle={id ? "Edit Check-In Question" : "Add Check-In Question"}
        navigation="Daily Check-In Questions"
        navlink="/daily-checkin-questions"
        current={id ? "Edit" : "Add"}
      />

      <div className="col-lg-10 mx-auto">
        <div className="card p-4 shadow-sm">
          <form onSubmit={handleSubmit(onSubmit)} className="row g-3">
            <div className="col-12 text-center">
              <h2>{id ? "Edit Question" : "Add New Question"}</h2>
            </div>

            {/* Question */}
            <div className="col-md-6">
              <label className="form-label">Question</label>
              <input
                className="form-control"
                {...register("question", {
                  required: "Question is required",
                })}
              />
              {errors.question && (
                <p className="text-danger">{errors.question.message}</p>
              )}
            </div>

            {/* Options */}
            <div className="col-12">
              <label className="form-label">Options</label>

              {options.map((opt, index) => (
                <div key={index} className="row g-2 mb-2 align-items-center">
                  {/* LABEL */}
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Option label (Yes / No etc.)"
                      value={opt.label}
                      onChange={(e) =>
                        handleOptionChange(index, "label", e.target.value)
                      }
                      required
                    />
                  </div>

                  {/* KEY */}
                  <div className="col-md-4">
                    <select
                      className="form-select"
                      value={opt.key}
                      onChange={(e) =>
                        handleOptionChange(index, "key", e.target.value)
                      }
                      required
                    >
                      <option value="">Select Key</option>
                      {CHECKIN_KEYS.map((k) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* VALUE */}
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={opt.value}
                      onChange={(e) =>
                        handleOptionChange(index, "value", e.target.value)
                      }
                      disabled={!opt.key}
                      required
                    >
                      <option value="">Select Value</option>
                      {opt.key &&
                        CHECKIN_VALUES_MAP[opt.key]?.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* REMOVE */}
                  <div className="col-md-1">
                    {options.length > 1 && (
                      <Button
                        variant="danger"
                        onClick={() => removeOption(index)}
                      >
                        ✕
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              <Button
                variant="success"
                onClick={addOption}
                className="mt-2"
              >
                Add Option
              </Button>
            </div>

            {/* Buttons */}
            <div className="col-12 text-center mt-4">
              <Button type="submit" className="btn btn-primary px-5 me-3">
                {id ? "Update" : "Save"}
              </Button>
              <Button
                variant="secondary"
                className="px-5"
                onClick={() => navigate("/daily-checkin-questions")}
              >
                Back
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddCheckinQuestionList;
